import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Button } from '@/shared/ui/button';
import { AlertTriangle, CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn, parseDateFromString } from '@/shared/utils/date-utils';
import { usePauseServiceLine } from '@/domains/service-lines/services/account-service-lines';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';

interface ServiceLinePauseDialogProps {
  open: boolean;
  serviceLine: ServiceLine;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const ServiceLinePauseDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine,
}: ServiceLinePauseDialogProps) => {
  // Get today's date for calendar restriction
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const plus60Days = new Date();
  plus60Days.setDate(plus60Days.getDate() + 60);

  const [formData, setFormData] = useState<{ resumptionDate?: Date | undefined }>({ resumptionDate: serviceLine?.resumesAt ? parseDateFromString(serviceLine?.resumesAt) : undefined });
  const pauseServiceLine = usePauseServiceLine();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await pauseServiceLine.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        resumesAt: formData.resumptionDate ? format(formData.resumptionDate, 'yyyy-MM-dd') : undefined,
      });
      showSuccess(SUCCESS.SERVICE_LINE.PAUSED);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      showErrorWithApiError(err, ERROR.SERVICE_LINE.PAUSE);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Pause Service Line</DialogTitle>
          <DialogDescription>
            Pause the service line to stop data usage.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Set End Date Toggle and Date Picker */}
          <div className="space-y-4">
            <Label htmlFor="pause-service-line-resumption-date">Resumption Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.resumptionDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.resumptionDate ? format(new Date(formData.resumptionDate), "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.resumptionDate ? new Date(formData.resumptionDate) : undefined}
                  onSelect={(date) => setFormData({ ...formData, resumptionDate: date ? date : undefined })}
                  disabled={(date) => date < tomorrow || date > plus60Days} // Only allow dates between tomorrow and the end of the current month
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="font text-red-500">Set resumption date, if not selected, the service line will be suspended after 60 days.</span>
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pauseServiceLine.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={pauseServiceLine.isPending}>
              {pauseServiceLine.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Pausing...
                </>
              ) : (
                'Pause Service Line'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
